import {useContractByCompany, useErrorLogsByCompany, useSuccessLogsByCompany} from '@api'
import {getUserData, isCompanyUser, isSuperAdmin, renderClickableCell, statusColorMap, useViewNavigate} from '@common'
import {ContractStatus, NavigationPathKey, TranslationKey} from '@enums'
import contractCreationImage from '@images/contract-creation-card.jpg'
import contractSearchImage from '@images/contract-search-card.jpg'
import creditCheckImage from '@images/credit-check-card.jpg'
import {useMediaQuery, useTheme} from '@mui/material'
import {GridColDef} from '@mui/x-data-grid'
import {pageLinksAtom, getUserSelectionAtom, getUserTimeFormatAtom} from '@state'

import {useEffect, useMemo} from 'react'

import {formatDate} from 'date-fns'
import {useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'

import {ComposedComponents} from '@components'

type ChartDataItem = {
  name: string
  [key: string]: number | string
}

export function useDashboardModel() {
  const navigate = useViewNavigate()
  const {t} = useTranslation()
  const currentUser = getUserData()
  const userSelections = useAtomValue(getUserSelectionAtom)
  const getUserTimeFormat = useAtomValue(getUserTimeFormatAtom)
  const userSelection = userSelections[currentUser.id]

  const userTimeFormat = getUserTimeFormat(currentUser.id)

  const showStatsSection = useMemo(() => isSuperAdmin(), [])

  const setTitleLinks = useSetAtom(pageLinksAtom)

  useEffect(() => setTitleLinks([{title: t(TranslationKey.Dashboard)}]), [])

  const {data: errorLogs} = useErrorLogsByCompany(userSelection?.companyId)
  const {data: successLogs} = useSuccessLogsByCompany(userSelection?.companyId)
  const {data: contractsByCompanyResponse} = useContractByCompany(userSelection?.companyId)

  const contractStatusConfig = [
    {
      status: ContractStatus.CANCELED,
      title: TranslationKey.Cancelled,
      color: statusColorMap.CANCELED,
    },
    {
      status: ContractStatus.DRAFT,
      title: TranslationKey.Draft,
      color: statusColorMap.DRAFT,
    },
    {
      status: ContractStatus.QUOTE,
      title: TranslationKey.Quote,
      color: statusColorMap.QUOTE,
    },
    {
      status: ContractStatus.ACTIVE,
      title: TranslationKey.Active,
      color: statusColorMap.ACTIVE,
    },
    {
      status: ContractStatus.SENT_FOR_SIGNING,
      title: TranslationKey['Sent for signing'],
      color: statusColorMap.SENT_FOR_SIGNING,
    },
    {
      status: ContractStatus.SIGNED,
      title: TranslationKey.Signed,
      color: statusColorMap.SIGNED,
    },
  ]

  const chartData = useMemo<ChartDataItem[]>(() => {
    if (!contractsByCompanyResponse) return []
    else {
      const dates = Array.from({length: 7}, (_, i) => {
        const date = new Date()
        date.setDate(date.getDate() - (6 - i))
        return String(date)
      })

      const datesWithContracts = dates.map(date => {
        const contractsByDate = contractsByCompanyResponse?.filter(
          item => formatDate(item.createdAt, 'dd-MM-yyyy') === formatDate(date, 'dd-MM-yyyy'),
        )

        return {
          date,
          contracts: contractsByDate,
        }
      })

      return datesWithContracts.map(item => ({
        name: formatDate(item.date, userTimeFormat),
        ...Object.fromEntries(
          contractStatusConfig.map(config => [
            t(config.title),
            item.contracts.filter(contract => contract.status === config.status).length,
          ]),
        ),
      }))
    }
  }, [contractsByCompanyResponse, userTimeFormat])

  const tableRows = useMemo(() => {
    if (!errorLogs || !successLogs) return []
    else
      return [
        ...successLogs.map(item => ({...item, id: item.contractId, success: true})),
        ...errorLogs.map(item => ({message: item.errorStep, createdAt: item.createdAt, id: '', success: false})),
      ]
        .map(item => ({
          message: item.success ? item.message.replace(item.id, '') : item.message,
          time: item.createdAt,
          id: item.id,
        }))
        .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
        .map(item => ({...item, time: formatDate(item.time, userTimeFormat)}))
        .slice(0, 5)
  }, [errorLogs, successLogs])

  const theme = useTheme()
  const isDownLg = useMediaQuery(theme.breakpoints.down('lg'))

  /**
   * Table columns for the Activity Logs section
   */
  const tableColumns: GridColDef[] = [
    {
      field: 'message',
      headerName: t(TranslationKey.Description),
      flex: 1,
      renderCell: params =>
        renderClickableCell(params, () => navigate(`${NavigationPathKey.CONTRACT_VIEW}/${params.row.id}`)),
    },
    {field: 'time', headerName: t(TranslationKey.Timestamp), flex: 0.5, align: 'right', headerAlign: 'right'},
  ]

  /**
   * Navigation Cards props
   */
  const navigationCardItems: ComposedComponents.CardItem[] = [
    {
      title: t(TranslationKey['Contract creation']),
      key: NavigationPathKey.CONTRACT_CREATION,
      image: contractCreationImage,
    },
    {
      title: t(TranslationKey['Contract search']),
      key: NavigationPathKey.CONTRACT_SEARCH,
      image: contractSearchImage,
    },
    ...(isCompanyUser()
      ? []
      : [
          {
            title: t(TranslationKey['Admin panel']),
            key: NavigationPathKey.ADMIN_PANEL,
            image: creditCheckImage,
          },
        ]),
  ]

  const noStatsData = useMemo(
    () =>
      !chartData
        .flatMap(item =>
          Object.keys(item)
            .filter(it => it !== 'name')
            .map(it => item[it]),
        )
        .some(item => Number(item) > 0),
    [chartData],
  )

  const domain = useMemo(() => {
    const values = chartData.flatMap(item => Object.values(item).filter((v): v is number => typeof v === 'number'))

    const maxValue = values.length ? Math.max(...values) : 0

    return [0, maxValue]
  }, [chartData])

  const bars = useMemo(
    () =>
      contractStatusConfig.map(item => ({
        dataKey: t(item.title),
        fill: item.color.text,
        radius: [4, 4, 0, 0] as [number, number, number, number],
      })),
    [],
  )

  return {
    navigationCardItems,
    tableColumns,
    tableRows,
    chartData,
    showStatsSection,
    domain,
    noStatsData,
    isDownLg,
    bars,
    t,
  }
}
